import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { getCurrentSeasonYear, getAvailableSeasons } from '../config/season';
import { supabase } from '../utils/supabase';

const SELECTED_SEASON_KEY = 'selectedSeason';
// Remembers which active season we last auto-migrated the user to, so that
// when an admin starts a NEW active season we can move the user forward
// (unless they've explicitly chosen to view an older season).
const AUTO_SEASON_KEY = 'autoMigratedActiveSeason';

interface SeasonContextType {
  /** The season the user is currently viewing. */
  selectedSeason: number;
  setSelectedSeason: (year: number) => void;
  /** All seasons the user can choose from (newest first). */
  availableSeasons: number[];
  /** The league-wide active season (source of truth from the database). */
  activeSeason: number;
  /** True when the selected season is the active season. */
  isCurrentSeason: boolean;
  /** Re-fetch active season + available seasons (e.g. after starting a season). */
  refreshSeasons: () => Promise<void>;
  loading: boolean;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

export function SeasonProvider({ children }: { children: ReactNode }) {
  const [selectedSeason, setSelectedSeasonState] = useState<number>(getCurrentSeasonYear());
  const [activeSeason, setActiveSeason] = useState<number>(getCurrentSeasonYear());
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  const loadSeasons = useCallback(async () => {
    // 1) Determine the league-wide active season (source of truth).
    let resolvedActive = getCurrentSeasonYear();
    try {
      const { data, error } = await supabase.rpc('get_active_season');
      if (!error && typeof data === 'number') {
        resolvedActive = data;
      }
    } catch (err) {
      console.error('Error fetching active season:', err);
    }
    setActiveSeason(resolvedActive);

    // 2) Determine which seasons the user can pick from (seasons with data),
    //    always including the active season even if it has no data yet.
    let seasons: number[] = [];
    try {
      const { data, error } = await supabase
        .from('league_bowler_week_stats')
        .select('season_year')
        .order('season_year', { ascending: false });

      if (error) {
        console.error('Error fetching available seasons:', error);
        seasons = getAvailableSeasons();
      } else if (data) {
        seasons = [...new Set(data.map((d) => d.season_year))];
      }
    } catch (err) {
      console.error('Error in loadSeasons:', err);
      seasons = getAvailableSeasons();
    }

    if (!seasons.includes(resolvedActive)) {
      seasons.push(resolvedActive);
    }
    seasons.sort((a, b) => b - a);
    if (seasons.length === 0) {
      seasons = getAvailableSeasons();
    }
    setAvailableSeasons(seasons);

    // 3) Decide the selected season (auto-migration logic):
    //    - If the user explicitly chose a season that still exists, respect it,
    //      UNLESS a new active season has been started since their last auto
    //      migration (then move them forward to the new active season).
    //    - Otherwise default to the active season.
    const storedSelected = parseInt(localStorage.getItem(SELECTED_SEASON_KEY) ?? '', 10);
    const lastAuto = parseInt(localStorage.getItem(AUTO_SEASON_KEY) ?? '', 10);
    const activeChangedSinceAuto = isNaN(lastAuto) || lastAuto !== resolvedActive;

    let nextSelected: number;
    if (!isNaN(storedSelected) && seasons.includes(storedSelected) && !activeChangedSinceAuto) {
      // Respect the user's explicit choice.
      nextSelected = storedSelected;
    } else {
      // Auto-migrate to (or default to) the active season.
      nextSelected = resolvedActive;
      localStorage.setItem(AUTO_SEASON_KEY, String(resolvedActive));
      localStorage.setItem(SELECTED_SEASON_KEY, String(resolvedActive));
    }
    setSelectedSeasonState(nextSelected);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadSeasons();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSeasons]);

  const setSelectedSeason = useCallback((year: number) => {
    setSelectedSeasonState(year);
    // Persist as an explicit user choice.
    localStorage.setItem(SELECTED_SEASON_KEY, year.toString());
  }, []);

  const refreshSeasons = useCallback(async () => {
    setLoading(true);
    await loadSeasons();
    setLoading(false);
  }, [loadSeasons]);

  const value: SeasonContextType = {
    selectedSeason,
    setSelectedSeason,
    availableSeasons,
    activeSeason,
    isCurrentSeason: selectedSeason === activeSeason,
    refreshSeasons,
    loading,
  };

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSeason() {
  const context = useContext(SeasonContext);
  if (context === undefined) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
}
